import React,{useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {motion} from 'framer-motion';
import {usePlanner} from '../context/PlannerContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiArrowLeft,FiArrowRight,FiUser,FiPlus,FiX}=FiIcons;

function DIBELSSurvey() {
  const navigate=useNavigate();
  const {state,dispatch}=usePlanner();
  const [students,setStudents]=useState(state.students.length > 0 ? state.students : [createEmptyStudent()]);
  const [currentStudentIndex,setCurrentStudentIndex]=useState(0);

  function createEmptyStudent() {
    return {
      name: '',
      fsf: '',
      psf: '',
      nwfCls: '',
      nwfWwr: '',
      orfWcpm: '',
      orfAccuracy: '',
      maze: ''
    };
  }

  const handleInputChange=(field,value)=> {
    const updatedStudents=[...students];
    updatedStudents[currentStudentIndex]={...updatedStudents[currentStudentIndex],[field]: value};
    setStudents(updatedStudents);
  };

  const addStudent=()=> {
    setStudents([...students,createEmptyStudent()]);
    setCurrentStudentIndex(students.length);
  };

  const deleteStudent=(indexToDelete)=> {
    if (students.length===1) return;
    const updatedStudents=students.filter((_,index)=> index !==indexToDelete);
    setStudents(updatedStudents);
    if (indexToDelete===currentStudentIndex) {
      setCurrentStudentIndex(indexToDelete > 0 ? indexToDelete - 1 : 0);
    } else if (indexToDelete < currentStudentIndex) {
      setCurrentStudentIndex(currentStudentIndex - 1);
    }
  };

  const selectStudent=(index)=> {
    setCurrentStudentIndex(index);
  };

  const processFormData=()=> {
    const processedStudents=students.map(student=> ({
      name: student.name,
      fsf: parseInt(student.fsf) || 0,
      psf: parseInt(student.psf) || 0,
      nwfCls: parseInt(student.nwfCls) || 0,
      nwfWwr: parseInt(student.nwfWwr) || 0,
      orfWcpm: parseInt(student.orfWcpm) || 0,
      orfAccuracy: parseInt(student.orfAccuracy) || 0,
      maze: parseInt(student.maze) || 0
    })).filter(student=> student.name.trim() !=='');

    dispatch({type: 'SET_ASSESSMENT_TYPE',payload: 'DIBELS'});
    dispatch({type: 'SET_STUDENTS',payload: processedStudents});
    navigate('/groups');
  };

  const isCurrentStudentValid=()=> {
    const currentStudent=students[currentStudentIndex];
    return currentStudent.name.trim() && Object.values(currentStudent).some(value=> value !=='' && value !==currentStudent.name);
  };

  const currentStudent=students[currentStudentIndex];

  return (
    <div className="min-h-screen">
      <motion.header
        initial={{opacity: 0,y: -20}}
        animate={{opacity: 1,y: 0}}
        className="bg-white shadow-sm border-b"
      >
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={()=> navigate('/assessment')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} />
              Back to Assessment Input
            </button>
            <h1 className="text-xl font-semibold text-thrive-slate font-display">DIBELS 8 Assessment</h1>
            <div className="w-20"></div>
          </div>
        </div>
      </motion.header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
        >
          <div className="bg-thrive-slate text-white px-6 py-4">
            <h2 className="text-xl font-bold text-center tracking-wider">DIBELS 8—Record Form</h2>
          </div>

          <div className="p-6">
            {/* Student Tabs */}
            {students.length > 1 && (
              <div className="flex flex-wrap gap-2 mb-6 pb-4 border-b border-gray-200">
                {students.map((student,index)=> (
                  <div key={index} className={`relative px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    currentStudentIndex===index 
                      ? 'bg-thrive-teal text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}>
                    <button onClick={()=> selectStudent(index)} className="pr-6">
                      {student.name || `Student ${index + 1}`}
                    </button>
                    {students.length > 1 && (
                      <button
                        onClick={(e)=> {e.stopPropagation();deleteStudent(index);}}
                        className="absolute top-1 right-1 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-colors"
                        title="Delete student"
                      >
                        <SafeIcon icon={FiX} className="text-xs" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Student Info */}
            <div className="mb-8 pb-6 border-b border-gray-200">
              <div className="max-w-md">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <SafeIcon icon={FiUser} className="inline mr-1" />
                  Student Name
                </label>
                <input
                  type="text"
                  value={currentStudent.name}
                  onChange={(e)=> handleInputChange('name',e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                  placeholder="Enter student name"
                />
              </div>
            </div>

            <h3 className="text-lg font-semibold text-thrive-slate mb-6 tracking-wider">DIBELS 8 MEASURES</h3>

            {/* DIBELS Assessment Grid */}
            <div className="space-y-4 mb-8">
              {[
                {field: 'fsf', label: 'First Sound Fluency (FSF)', unit: 'sounds'},
                {field: 'psf', label: 'Phoneme Segmentation Fluency (PSF)', unit: 'phonemes'},
                {field: 'nwfCls', label: 'Nonsense Word Fluency - Correct Letter Sounds (NWF-CLS)', unit: 'sounds'},
                {field: 'nwfWwr', label: 'Nonsense Word Fluency - Whole Words Read (NWF-WWR)', unit: 'words'},
                {field: 'orfWcpm', label: 'Oral Reading Fluency - Words Correct Per Minute (ORF-WCPM)', unit: 'words'},
                {field: 'orfAccuracy', label: 'Oral Reading Fluency - Accuracy (ORF)', unit: '%'},
                {field: 'maze', label: 'DIBELS 8 Maze', unit: 'correct'}
              ].map((measure,index)=> (
                <div key={index} className="flex items-center gap-4">
                  <div className="w-20 text-center">
                    <input
                      type="number"
                      min="0"
                      value={currentStudent[measure.field]}
                      onChange={(e)=> handleInputChange(measure.field,e.target.value)}
                      className="w-full px-2 py-2 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                    />
                    <div className="text-xs text-gray-500 mt-1">{measure.unit}</div>
                  </div>
                  <div className="flex-1">
                    <span className="font-medium mr-2">{index + 1}.</span>
                    <span>{measure.label}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-6 border-t border-gray-200">
              <button
                onClick={addStudent}
                className="bg-thrive-mint hover:bg-thrive-teal text-thrive-slate hover:text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 shadow-md"
              >
                <SafeIcon icon={FiPlus} />
                Add Student
              </button>
              <button
                onClick={processFormData}
                disabled={!isCurrentStudentValid()}
                className="bg-thrive-teal hover:bg-thrive-slate disabled:bg-gray-300 text-white px-8 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 shadow-lg disabled:shadow-none"
              >
                Continue to Groups
                <SafeIcon icon={FiArrowRight} />
              </button>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
}

export default DIBELSSurvey;